"use client"

import { useEffect, useState, useTransition } from "react"
import { Modal, Stack, Group, Text, Button, Divider, Alert, Center, Loader, Anchor, Radio, Badge, Paper } from "@forestsync/ui"
import { TbCreditCard, TbBuildingBank, TbAlertTriangle } from "react-icons/tb"
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"
import { useTranslations } from "next-intl"
import { useRouter } from "next/navigation"
import {
  createTradePaymentIntent,
  cancelTradePaymentIntent,
  createTraderBalancePaymentIntent,
  getTraderBalance,
} from "@/actions/trade-payment"
import { submitTradeOrder } from "@/actions/trading"
import { TRADE_ORDER_SIDE, TRADE_ORDER_TIME_IN_FORCE, TRADE_ORDER_TYPE } from "@forestsync/db"
import { nextSessionOpen, isMarketOpen } from "@forestsync/lib/formatting"
import type { TradeFill } from "./TradeBlotter"

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!)

// Stripe card processing fee estimate: 1.5% + €0.25 (EU cards)
// Non-EU cards are higher (~2.5%) but we show the conservative estimate
function estimateStripeFee(amountEur: number): number {
  return Math.round((amountEur * 0.015 + 0.25) * 100) / 100
}

type PaymentMethod = "balance" | "card"

type OrderInput = Parameters<typeof submitTradeOrder>[0]

interface OrderConfirmModalProps {
  opened: boolean
  onClose: () => void
  orderInput: OrderInput | null
  breakdown: { subtotal: number; platformFee: number; totalDue: number } | null
  onSuccess: (fill: TradeFill | null) => void
  onProfileRequired: () => void
}

interface CardPaymentFormProps {
  paymentIntentId: string
  isBid: boolean
  isPending: boolean
  onPlaceOrder: (intentId: string) => void
  onError: (msg: string) => void
  onClose: () => void
}

function CardPaymentForm({
  paymentIntentId,
  isBid,
  isPending,
  onPlaceOrder,
  onError,
  onClose,
}: CardPaymentFormProps) {
  const t = useTranslations("trading")
  const stripe = useStripe()
  const elements = useElements()
  const [localPending, startTransition] = useTransition()

  function handleConfirm() {
    if (!stripe || !elements) { onError(t("orderError")); return }
    startTransition(async () => {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {},
        redirect: "if_required",
      })
      if (result.error) { onError(result.error.message ?? t("orderError")); return }
      onPlaceOrder(result.paymentIntent?.id ?? paymentIntentId)
    })
  }

  return (
    <>
      <PaymentElement />
      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>
          {t("no")}
        </Button>
        <Button
          color={isBid ? "green" : "red"}
          loading={isPending || localPending}
          onClick={handleConfirm}
          autoContrast
        >
          {t("authorizeAndPlace")}
        </Button>
      </Group>
    </>
  )
}

export function OrderConfirmModal({
  opened,
  onClose,
  orderInput,
  breakdown,
  onSuccess,
  onProfileRequired,
}: OrderConfirmModalProps) {
  const t = useTranslations("trading")
  const router = useRouter()
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("card")
  const [balanceCents, setBalanceCents] = useState<number | null>(null)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null)
  const [loadingPayment, setLoadingPayment] = useState(false)
  const [loadingBalance, setLoadingBalance] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isPending, startTransition] = useTransition()

  const isBid = orderInput?.side === TRADE_ORDER_SIDE.BID
  const isDayOrder = !!(orderInput?.orderTimeInForce === TRADE_ORDER_TIME_IN_FORCE.DAY && orderInput?.orderType !== 2)
  const isGtcLimitOrStop = !!(orderInput && orderInput.orderType !== 2 && orderInput.orderTimeInForce !== TRADE_ORDER_TIME_IN_FORCE.DAY)
  const sessionOpen = isMarketOpen()
  const dayExpiresAt = isDayOrder ? nextSessionOpen() : null
  const totalDue = breakdown?.totalDue ?? 0
  const stripeFeeEstimate = estimateStripeFee(totalDue)
  const balanceEur = balanceCents !== null ? balanceCents / 100 : null
  const balanceSufficient = balanceEur !== null && balanceEur >= totalDue

  // Fetch balance on open for BID orders
  useEffect(() => {
    if (!opened || !isBid || totalDue <= 0) return
    setLoadingBalance(true)
    setBalanceCents(null)
    getTraderBalance().then((res) => {
      if ("balanceCents" in res) {
        setBalanceCents(res.balanceCents)
        // Default to balance if sufficient, otherwise card
        setPaymentMethod(res.balanceCents / 100 >= totalDue ? "balance" : "card")
      } else {
        setPaymentMethod("card")
      }
      setLoadingBalance(false)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, isBid])

  // Create card PaymentIntent when card method is selected
  useEffect(() => {
    if (!opened || !isBid || paymentMethod !== "card" || totalDue <= 0 || (orderInput?.orderType === TRADE_ORDER_TYPE.MARKET && !isMarketOpen())) return
    let active = true
    setLoadingPayment(true)
    setError(null)
    setClientSecret(null)
    setPaymentIntentId(null)

    createTradePaymentIntent({ amountEur: totalDue }).then((result) => {
      if (!active) return
      if ("error" in result) {
        setError(result.error ?? t("orderError"))
        setLoadingPayment(false)
        return
      }
      setClientSecret(result.clientSecret)
      setPaymentIntentId(result.paymentIntentId)
      setLoadingPayment(false)
    })

    return () => { active = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, isBid, paymentMethod, totalDue])

  function handleClose() {
    setError(null)
    setClientSecret(null)
    setPaymentIntentId(null)
    setBalanceCents(null)
    onClose()
  }

  function handleMethodChange(method: PaymentMethod) {
    setPaymentMethod(method)
    setError(null)
    setClientSecret(null)
    setPaymentIntentId(null)
  }

  function handlePaymentAuthorized(confirmedPiId: string) {
    placeOrder(confirmedPiId)
  }

  function handleBalanceConfirm() {
    if (!orderInput) return
    setError(null)
    startTransition(async () => {
      const result = await createTraderBalancePaymentIntent({ amountEur: totalDue })
      if ("error" in result) { setError(result.error ?? t("orderError")); return }
      placeOrder(result.paymentIntentId)
    })
  }

  function handleAskConfirm() {
    if (!orderInput) return
    setError(null)
    startTransition(async () => { placeOrder(undefined) })
  }

  async function placeOrder(intentId: string | undefined) {
    if (!orderInput) return
    const result = await submitTradeOrder({
      ...orderInput,
      paymentIntentId: intentId,
    })
    if ("error" in result) {
      if (intentId) {
        await cancelTradePaymentIntent({ paymentIntentId: intentId }).catch(() => null)
        // The PaymentIntent above is now cancelled server-side — never leave a dead one
        // mounted in the card form. A retry would call stripe.confirmPayment() against
        // an already-cancelled PI, which Stripe can only report as a generic "processing
        // error." Clearing these lets the useEffect mint a fresh PaymentIntent for retry.
        setClientSecret(null)
        setPaymentIntentId(null)
      }
      const code = (result as { error: string; code?: string }).code
      if (code === "PROFILE_REQUIRED") {
        handleClose()
        onProfileRequired()
      } else if (code === "MARKET_CLOSED") {
        setError(`${t("marketClosed")} — ${t("marketClosedDesc")}`)
      } else if (code === "PAYMENT_METHOD_REQUIRED") {
        setError("PAYMENT_METHOD_REQUIRED")
      } else {
        setError(result.error ?? t("orderError"))
      }
      return
    }
    const fill: TradeFill | null =
      result.order.filledVolume > 0
        ? {
            id: result.order.refId,
            time: new Date(),
            side: orderInput.side,
            price: result.order.price ?? orderInput.price ?? 0,
            volume: result.order.filledVolume,
            aggressorSide: null,
          }
        : null
    onSuccess(fill)
  }

  const showPaymentSelector = isBid && totalDue > 0

  return (
    <Modal opened={opened} onClose={handleClose} title={t("confirmOrder")} size="md" centered>
      <Stack gap="md">
        {/* Cost breakdown */}
        {breakdown && (
          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="sm" c="dimmed">{t("subtotal")}</Text>
              <Text size="sm">€{breakdown.subtotal.toFixed(2)}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">{t("platformFee")}</Text>
              <Text size="sm">€{breakdown.platformFee.toFixed(2)}</Text>
            </Group>
            <Divider my={2} />
            <Group justify="space-between">
              <Text size="sm" fw={700}>{t("totalDue")}</Text>
              <Text size="sm" fw={700}>€{breakdown.totalDue.toFixed(2)}</Text>
            </Group>
          </Stack>
        )}

        {dayExpiresAt && (
          <Text size="xs" c="dimmed">
            {t("dayOrderExpiry", {
              date: dayExpiresAt.toLocaleString(undefined, {
                timeZone: "Europe/Oslo",
                weekday: "short",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                timeZoneName: "short",
              }),
            })}
          </Text>
        )}

        {/* Off-hours info banner for queued LIMIT/STOP orders */}
        {!sessionOpen && isGtcLimitOrStop && (
          <Alert color="blue" variant="light" p="xs">
            {t("orderQueuedForOpen", {
              time: nextSessionOpen().toLocaleString(undefined, {
                timeZone: "Europe/Oslo",
                weekday: "short",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                timeZoneName: "short",
              }),
            })}
          </Alert>
        )}
        {!sessionOpen && isDayOrder && (
          <Alert color="blue" variant="light" p="xs">
            {t("dayOrderActivatesAtOpen", {
              time: nextSessionOpen().toLocaleString(undefined, {
                timeZone: "Europe/Oslo",
                weekday: "short",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                timeZoneName: "short",
              }),
            })}
          </Alert>
        )}

        {/* Payment method selector — only for BID orders with amount due */}
        {showPaymentSelector && (
          <>
            <Divider label={t("paymentMethod")} labelPosition="center" />

            {loadingBalance ? (
              <Center h={60}><Loader size="sm" /></Center>
            ) : (
              <Stack gap="xs">
                {/* Balance option */}
                <Paper
                  withBorder
                  p="sm"
                  style={{
                    cursor: "pointer",
                    borderColor: paymentMethod === "balance" ? "var(--mantine-color-teal-6)" : undefined,
                  }}
                  onClick={() => handleMethodChange("balance")}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="xs" wrap="nowrap">
                      <Radio
                        checked={paymentMethod === "balance"}
                        onChange={() => handleMethodChange("balance")}
                        color="teal"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <TbBuildingBank size={16} />
                      <Stack gap={2}>
                        <Text size="sm" fw={500}>{t("accountBalance")}</Text>
                        <Text size="xs" c="dimmed">
                          {t("balance")}{": "}
                          {balanceEur !== null
                            ? `€${balanceEur.toFixed(2)}`
                            : t("noBalance")}
                        </Text>
                      </Stack>
                    </Group>
                    <Badge color="teal" variant="light" size="sm">
                      {t("noProcessingFee")}
                    </Badge>
                  </Group>
                  {!balanceSufficient && balanceEur !== null && (
                    <Text size="xs" c="orange" mt={6} ml={40}>
                      {t("insufficientBalanceHint", { amount: `€${(totalDue - balanceEur).toFixed(2)}` })}{" "}
                      <Anchor
                        component="button"
                        size="xs"
                        fw={600}
                        onClick={(e) => { e.stopPropagation(); handleClose(); router.push("/dashboard/settings") }}
                      >
                        {t("fundAccount")}
                      </Anchor>
                    </Text>
                  )}
                </Paper>

                {/* Card option */}
                <Paper
                  withBorder
                  p="sm"
                  style={{
                    cursor: "pointer",
                    borderColor: paymentMethod === "card" ? "var(--mantine-color-blue-6)" : undefined,
                  }}
                  onClick={() => handleMethodChange("card")}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="xs" wrap="nowrap">
                      <Radio
                        checked={paymentMethod === "card"}
                        onChange={() => handleMethodChange("card")}
                        color="blue"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <TbCreditCard size={16} />
                      <Stack gap={2}>
                        <Text size="sm" fw={500}>{t("cardPayment")}</Text>
                        <Text size="xs" c="dimmed">{t("cardPaymentSub")}</Text>
                      </Stack>
                    </Group>
                    <Group gap={4} wrap="nowrap">
                      <TbAlertTriangle size={12} color="var(--mantine-color-orange-6)" />
                      <Text size="xs" c="orange" fw={500}>
                        +€{stripeFeeEstimate.toFixed(2)} {t("stripeFeeLabel")}
                      </Text>
                    </Group>
                  </Group>
                </Paper>
              </Stack>
            )}

            {/* Card payment form (only when card is selected) */}
            {paymentMethod === "card" && (
              <>
                {loadingPayment && <Center h={120}><Loader size="sm" /></Center>}
                {clientSecret && (
                  <Elements stripe={stripePromise} options={{ clientSecret }}>
                    <CardPaymentForm
                      paymentIntentId={paymentIntentId!}
                      isBid={isBid}
                      isPending={isPending}
                      onPlaceOrder={handlePaymentAuthorized}
                      onError={setError}
                      onClose={handleClose}
                    />
                  </Elements>
                )}
              </>
            )}

            {/* Balance confirm button */}
            {paymentMethod === "balance" && !loadingBalance && (
              <Group justify="flex-end">
                <Button variant="default" onClick={handleClose}>
                  {t("no")}
                </Button>
                <Button
                  color="green"
                  loading={isPending}
                  disabled={!balanceSufficient}
                  onClick={handleBalanceConfirm}
                  autoContrast
                >
                  {t("authorizeAndPlace")}
                </Button>
              </Group>
            )}
          </>
        )}

        {/* ASK orders — no payment needed */}
        {!isBid && (
          <Group justify="flex-end">
            <Button variant="default" onClick={handleClose}>
              {t("no")}
            </Button>
            <Button
              color="red"
              loading={isPending}
              onClick={handleAskConfirm}
              autoContrast
            >
              {t("placeOrder")}
            </Button>
          </Group>
        )}

        {error && (
          <Alert
            color="red"
            icon={<TbAlertTriangle size={16} />}
            onClose={() => setError(null)}
            withCloseButton
          >
            {error === "PAYMENT_METHOD_REQUIRED" ? (
              <span>
                {t("addPaymentMethod")}{" "}
                <Anchor
                  component="button"
                  fw={600}
                  onClick={() => { handleClose(); router.push("/dashboard/settings") }}
                >
                  {t("goToSettings")}
                </Anchor>
              </span>
            ) : error.startsWith("Insufficient balance") ? (
              <span>
                {error.split("Fund your account")[0]}
                <Anchor
                  component="button"
                  fw={600}
                  onClick={() => { handleClose(); router.push("/dashboard/settings") }}
                >
                  {t("fundAccount")}
                </Anchor>
                {error.split("Fund your account")[1]}
              </span>
            ) : error}
          </Alert>
        )}
      </Stack>
    </Modal>
  )
}
